import React, { FC, useCallback, useEffect, useState } from 'react';
import { CurrencyLabel } from '../../composite/CurrencyLabel';
import { Skeleton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useBidding } from "prembid-shared-library-npm/hooks";
import { useSignalRConnectionStatus } from 'prembid-shared-library-npm/signalr';
import { AuctionTypeEnum } from 'prembid-shared-library-npm/types';
import BidButtonContainer from './sub-components/BidButtonContainer';
import CustomBidButtonContainer from './sub-components/CustomBidButtonContainer';
import MaxBidButtonContainer from './sub-components/MaxBidButtonContainer';
import TenderBidButtonContainer from './sub-components/TenderBidButtonContainer';
import GuidePrice from './sub-components/GuidePrice';
import ReserveMet from './sub-components/ReserveMet';
import TableOfBids from './sub-components/TableOfBids';
import PaddleNumber from './sub-components/PaddleNumber';
import { uniqueId } from 'lodash';
import ns from '@/lib/utils/NotificationService';
import CountdownTimerContainer from './sub-components/CountdownTimerContainer';
import { RootState } from '../../../store/Store';
import { setLotClosingValidating } from '../../../pages/LotBidAndInfo/store/LotBidAndInfo';
import ParticipateButton from '../../Buttons/ParticipateButton';

interface BiddingTableProps {
    auctionId: string;
    lotId: string;
    showCompactView: boolean;
    // Called with the amount once a quick or custom bid is accepted, so a lot card can show it straight away instead of
    // waiting for the live update (as on mobile).
    onBidPlaced?: (amount: number) => void;
}

const BiddingTable: FC<BiddingTableProps> = ({ auctionId, lotId, showCompactView, onBidPlaced }) => {

    const bidTableRef = React.useRef<HTMLDivElement>(null);
    const [bidMode, setBidMode] = useState<"quick" | "custom" | "max">("quick");
    const dispatch = useDispatch();

    const profileId = useSelector((state: RootState) => state.loginForm.changeProfileId);
    const currentDateTimeOffset = useSelector((state: RootState) => state.settings.serverDateTimeDifference);
    const { settings } = useSelector((state: RootState) => state.settings);

    const scrollToTop = useCallback(() => {
        bidTableRef.current?.scrollTo({ top: 0 });
    }, []);

    const {
        auction,
        lot,
        now,
        maximumBidAmount,
        tenderAmount,
        paddleNumber,
        registrationStatus,
        soldBanner,
        locked,
        startingPrice,
        validating,
        countDownTimer,
        topBidder,
        reserveMet,
        minimumIncrement,
        loading,
        loadingRegisteredProfile,
        finalizingText,
        lockedText,
        unlockedText,
        refresh,
        handleBidSubmittedSuccess,
        handleMaxBidSubmittedSuccess,
        handleTenderSubmittedSuccess,
        lotStatusFailureCount,
        error,
        end,
        start,
        tableRowData,
        isExtended,
    } = useBidding({
        auctionId,
        lotId,
        profileId,
        currentDateTimeOffset,
        locales: {
            lotName: settings.LotName,
        },
        onScrollToTop: scrollToTop,
    });

    // Accepted quick/custom bid: show it in this panel (bid table and highest bid) and tell the caller right away.
    const handleBidPlaced = useCallback((amount: number, paddleNumber: string) => {
        handleBidSubmittedSuccess(amount, paddleNumber);
        onBidPlaced?.(Number(amount));
    }, [handleBidSubmittedSuccess, onBidPlaced]);

    useEffect(() => {
        if (bidTableRef.current && tableRowData?.length) {
            scrollToTop();
        }
    }, [tableRowData, scrollToTop]);

    useSignalRConnectionStatus({
        onConnect: () => {
            refresh();
        },
    });

    useEffect(() => {
        if (locked?.isLocked === false && unlockedText) ns.information(uniqueId(), unlockedText);
        else if (locked?.isLocked === true && lockedText) ns.information(uniqueId(), lockedText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locked]);

    useEffect(() => {
        if (error) ns.error(uniqueId(), error);
    }, [error]);

    useEffect(() => {
        dispatch(setLotClosingValidating({
            lotId: lotId,
            validating: validating,
            validatingText: finalizingText,
            validatingErrorText: lotStatusFailureCount > 3 ? `Unable to connect, retrying(${lotStatusFailureCount - 3})...` : undefined,
            soldBanner: {
                showBanner: soldBanner?.showBanner,
                bannerText: soldBanner?.bannerText
            }
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validating, soldBanner?.showBanner]);

    const showLoading = loading || loadingRegisteredProfile;
    const canBid = auction?.auctionType?.toString() === AuctionTypeEnum.Timed.toString() && (validating || (end && end > now)) && registrationStatus === 1;
    const isTender = auction?.auctionType === AuctionTypeEnum.Tender;

    // One bid control at a time, picked with the segmented switch (as on mobile).
    const bidModes = [
        { key: "quick", label: "Quick bid" },
        { key: "custom", label: "Custom" },
        { key: "max", label: "Max bid" },
    ] as const;

    if (auction?.auctionType?.toString() === AuctionTypeEnum.Streamed.toString()) return null;

    // Quick bid popover: a skeleton in the shape of the loaded panel, so it opens at full size and does not jump.
    if (showCompactView && showLoading) {
        return (
            <div className="pb-bid pb-bid--compact" aria-busy="true">
                <div style={{ paddingRight: 36 }}>
                    <Skeleton animation="wave" variant="text" width={80} height={14} />
                    <Skeleton animation="wave" variant="text" width={170} height={30} />
                    <Skeleton animation="wave" variant="text" width={130} height={14} />
                </div>
                <Skeleton animation="wave" variant="rounded" height={38} style={{ borderRadius: 999 }} />
                <Skeleton animation="wave" variant="rounded" height={50} style={{ borderRadius: 999 }} />
                <Skeleton animation="wave" variant="text" width={190} height={18} style={{ alignSelf: "center" }} />
            </div>
        );
    }

    return (
        <div className={"pb-bid" + (showCompactView ? " pb-bid--compact" : "")}>
            {!showCompactView && (
                <CountdownTimerContainer
                    showLoading={showLoading}
                    auctionType={auction?.auctionType}
                    isActive={lot?.isActive}
                    validating={validating}
                    isExtended={isExtended}
                    isWinning={topBidder?.bidder === paddleNumber}
                    profileId={profileId}
                    start={start}
                    end={end}
                    now={now}
                    countDownTimer={countDownTimer} />
            )}

            {!isTender && !showLoading && (
                <div className="pb-bid-summary">
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="pb-bid-summary-label">Highest bid</div>
                        <div className="pb-bid-summary-value">
                            {topBidder?.amount ? <CurrencyLabel value={topBidder.amount} textColour="var(--ink)" /> : "No bids yet"}
                        </div>
                        <div className="pb-bid-summary-hint">
                            <CurrencyLabel value={minimumIncrement} prefixString="Min increment" textColour="var(--faint)" />
                        </div>
                    </div>
                    <div className="pb-bid-summary-actions">
                        <ParticipateButton part="status" auctionId={auctionId} auctionType={auction?.auctionType} />
                    </div>
                </div>
            )}

            {!isTender && canBid && (
                <>
                    <div className="pb-segment" role="tablist">
                        {bidModes.map((m) => (
                            <button
                                key={m.key}
                                type="button"
                                role="tab"
                                aria-selected={bidMode === m.key}
                                className={"pb-segment-item" + (bidMode === m.key ? " pb-segment-item--active" : "")}
                                onClick={() => setBidMode(m.key)}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>

                    <div className="pb-bid-controls">
                        {bidMode === "quick" && (
                            <BidButtonContainer
                                showLoading={showLoading}
                                commission={lot?.commission}
                                biddingCalculation={lot?.biddingCalculation}
                                increment={minimumIncrement}
                                topBidderAmount={topBidder?.amount}
                                startingPrice={startingPrice}
                                auctionId={auctionId}
                                lotId={lotId}
                                onSuccess={handleBidPlaced}
                                belowButton={<ParticipateButton part="compliance" auctionId={auctionId} auctionType={auction?.auctionType} />}
                            />
                        )}
                        {bidMode === "custom" && (
                            <CustomBidButtonContainer
                                showLoading={showLoading}
                                commission={lot?.commission}
                                biddingCalculation={lot?.biddingCalculation}
                                auctionId={auctionId}
                                lotId={lotId}
                                onSuccess={handleBidPlaced}
                            />
                        )}
                        {bidMode === "max" && (
                            <MaxBidButtonContainer
                                showLoading={showLoading}
                                commission={lot?.commission}
                                biddingCalculation={lot?.biddingCalculation}
                                increment={minimumIncrement}
                                topBidderAmount={topBidder?.amount}
                                startingPrice={startingPrice}
                                auctionId={auctionId}
                                lotId={lotId}
                                onSuccess={handleMaxBidSubmittedSuccess}
                                maximumBidAmount={maximumBidAmount}
                            />
                        )}
                    </div>
                </>
            )}

            {isTender && (validating || (end && end > now)) && registrationStatus === 1 && (
                <div className="pb-bid-controls">
                    <TenderBidButtonContainer
                        showLoading={showLoading}
                        auctionId={auctionId}
                        lotId={lotId}
                        onSuccess={handleTenderSubmittedSuccess}
                        tenderAmount={tenderAmount}
                    />
                </div>
            )}

            <div className="pb-bid-register">
                <ParticipateButton
                    part={isTender ? "action" : "register"}
                    showLoading={showLoading}
                    auctionId={auctionId}
                    auctionType={auction?.auctionType}
                />
                {!(!isTender && canBid && bidMode === "quick") && <ParticipateButton part="compliance" auctionId={auctionId} auctionType={auction?.auctionType} />}
            </div>

            {!showCompactView && (
                <div className="pb-bid-details">
                    <ReserveMet showLoading={showLoading} reserveMet={reserveMet} showReserve={lot?.showReserve} />
                    <GuidePrice showLoading={showLoading} guidePrice={lot?.guidePrice} registrationStatus={registrationStatus} />
                    {!isTender && <PaddleNumber showLoading={showLoading} paddleNumber={paddleNumber} />}
                    {!isTender && <TableOfBids showLoading={showLoading} tableRowData={tableRowData || []} showPaddleNumber={lot?.showPaddleNumber || false} paddleNumber={paddleNumber} bidTableRef={bidTableRef} />}
                </div>
            )}

        </div>
    );
};

export default BiddingTable;